import type { CognitoUser } from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";
import { CreateOrganisationBody } from "components/user/sign-in/onboarding-wizard";
import { UserMembership } from "data/models";
import { fetchUserMemberships } from "data/queries";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { useQuery } from "react-query";
import { IntercomProps } from "react-use-intercom";
import { Either, left, right } from "utils";
import { makeApiRequestPostToUrl } from "utils/api-client";
import { OrganisationRow } from "./models";

import { SignOutOpts } from "@aws-amplify/auth/lib-esm/types";
import * as QueryKeys from "data";

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  organisations: Organisation[];
  cognitoSub: string; // the OAUTH2.0 cognito subject
  cognitoUser: CognitoUser;
  accessibleCount: boolean;
  onboarding: boolean;
  hasPendingInvitations: boolean;
  onboardingComplete: string;
  phoneNumber: string;
  role: string;
  workPhone: string;
  emailVerified: boolean;
  phoneNumberVerified: boolean;
  address: string;
}

export interface Organisation {
  name: string;
  organisationId: string;
  active: boolean;
  abn?: string;
  subscription?: string;
  hasAccess?: boolean; // Whether user can actually access this org
}

// represents the state of identify of a user using the portal, who they are and what they're acting as
export type IdentityState = User | false | "loading";

export const GetUserIfLoggedIn = (
  identityState: IdentityState
): User | false => {
  if (identityState === "loading" || identityState === false) {
    return false;
  }
  return identityState;
};

export const isAdminIdentity = (identityState: IdentityState): boolean => {
  const user = GetUserIfLoggedIn(identityState);
  if (user !== false) {
    return user.isAdmin;
  }
  return false;
};

export const UserContext = createContext<
  [IdentityState, Dispatch<SetStateAction<IdentityState>> | undefined]
>([false, undefined]);

export const switchedOrganisationCookieName = "switched-organisation";

export function useIdentity(): [
  IdentityState,
  Dispatch<SetStateAction<IdentityState>>
] {
  // @ts-ignore: might be able to fix this later, need to more understand context typing (could be createContext<[S, D?]>)
  const [userState, setUserState] = useContext(UserContext) as [
    IdentityState,
    Dispatch<SetStateAction<IdentityState>>
  ];

  return [userState, setUserState];
}

export const IdentityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [identityState, setIdentityState] = useState<IdentityState>("loading");

  const [cookies, setCookies, removeCookie] = useCookies([
    switchedOrganisationCookieName,
  ]);

  const organisationCookie: Organisation | undefined =
    cookies[switchedOrganisationCookieName];

  const membershipsQuery = useQuery({
    queryKey: [identityState, QueryKeys.userMembership],
    queryFn: () => fetchUserMemberships(true), // Fetch with byEmail=true to get pending invites
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    onError: () => {
      setIdentityState(false);
    },
    onSuccess: async (membership: UserMembership[]) => {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();

        // Include ALL accepted orgs, but preserve hasAccess flag
        const allAcceptedOrgs: Organisation[] = membership
          .filter((o) => o.status === "Accepted")
          .map((o) => ({
            name: o.organisationName,
            organisationId: o.organisationId,
            active: false,
            subscription: o.subscription,
            hasAccess: o.hasAccess !== false,
          }));

        // Count pending invitations
        const pendingInvitations = membership.filter(
          (o) => o.status === "Pending"
        );

        setIdentityState(
          convertToUser(
            cognitoUser,
            allAcceptedOrgs,
            organisationCookie,
            pendingInvitations.length
          )
        );
      } catch {
        setIdentityState(false);
      }
    },
  });

  const user = GetUserIfLoggedIn(identityState);

  const activeOrg = user
    ? user.organisations.find((x) => x.active === true)
    : null;

  useEffect(() => {
    if (activeOrg) {
      if (activeOrg?.organisationId === organisationCookie?.organisationId) {
        return;
      }
      setCookies(switchedOrganisationCookieName, activeOrg, {
        path: "/",
      });
    }
  }, [activeOrg]);

  const [id, setId] = useMemo(
    () => [identityState, setIdentityState],
    [identityState]
  );

  return (
    <UserContext.Provider value={[id, setId]}>{children}</UserContext.Provider>
  );
};
export type Props = {
  children: ReactNode;
  context: IdentityState;
};

const convertToUser = (
  cognitoUser: CognitoUser,
  organisations: Organisation[],
  savedOrganisation?: Organisation,
  pendingInvitationsCount: number = 0
): User => {
  const dynamicUser = cognitoUser as any;
  const isAdmin =
    dynamicUser.signInUserSession.accessToken.payload.hasOwnProperty(
      "cognito:groups"
    ) &&
    dynamicUser.signInUserSession.accessToken.payload["cognito:groups"][0] ===
      "admin";

  // Calculate accessible org count if not provided
  const accessibleCount = organisations.filter(
    (o) => o.hasAccess !== false
  ).length;

  const user = {
    firstName: dynamicUser.attributes.given_name,
    lastName: dynamicUser.attributes.family_name,
    email: dynamicUser.attributes.email,
    cognitoSub: dynamicUser.attributes.sub,
    isAdmin: isAdmin,
    organisations:
      organisations.length === 0
        ? []
        : setActiveOrganisation(organisations, savedOrganisation),
    cognitoUser: dynamicUser,
    onboarding: organisations.length === 0, // User has NO accepted organisations
    onboardingComplete: dynamicUser.attributes["custom:onboardingComplete"],
    accessibleCount: accessibleCount !== 0,
    hasPendingInvitations: pendingInvitationsCount > 0,
    phoneNumber: dynamicUser.attributes.phone_number,
    role: dynamicUser.attributes["custom:role"],
    workPhone: dynamicUser.attributes["custom:work_phone"],
    phoneNumberVerified: dynamicUser.attributes.phone_number_verified,
    emailVerified: dynamicUser.attributes.email_verified,
    address: dynamicUser.attributes.address,
  };

  return user;
};

export async function isAdmin() {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const admin =
    cognitoUser.signInUserSession.accessToken.payload["cognito:groups"] &&
    cognitoUser.signInUserSession.accessToken.payload["cognito:groups"][0] ===
      "admin";

  return admin;
}

export const signUpUser = async (
  email: string,
  firstName: string,
  lastName: string,
  password: string,
  recaptchaToken: string
): Promise<Either<boolean, Error>> => {
  try {
    await Auth.signUp({
      username: email.toLowerCase(),
      password: password,
      attributes: {
        given_name: firstName,
        family_name: lastName,
        "custom:dev_t_and_c": String(true),
      },
      validationData: {
        recaptcha_token: recaptchaToken,
      },
    });
    return left(true);
  } catch (error: any) {
    return right(error);
  }
};

export const convertToHubUser = async (
  cognitoUser: CognitoUser
): Promise<User> => {
  const membership: UserMembership[] = await fetchUserMemberships(false);

  const allAcceptedOrgs: Organisation[] = membership
    .filter((o) => o.status === "Accepted")
    .map((o) => {
      return {
        name: o.organisationName,
        organisationId: o.organisationId,
        active: false,
        subscription: o.subscription,
        hasAccess: o.hasAccess !== false, // Preserve access status
      };
    });

  const pendingInvitations = membership.filter((o) => o.status === "Pending");

  return convertToUser(
    cognitoUser,
    allAcceptedOrgs,
    undefined,
    pendingInvitations.length
  );
};

export const userCreateOrganisation = async (
  createOrg: CreateOrganisationBody
): Promise<OrganisationRow> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation`,
      "POST",
      createOrg
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Failed to create organisation: ${err}`);
  }
};

export const signOut = async (
  removeCookie: (
    name: typeof switchedOrganisationCookieName,
    options?: any
  ) => void,
  opts?: SignOutOpts
): Promise<Either<boolean, Error>> => {
  try {
    await Auth.signOut(opts);
    removeCookie(switchedOrganisationCookieName);
    return left(true);
  } catch (error: any) {
    return right(error);
  }
};

export const getActiveOrganisation = (
  identity: IdentityState
): Organisation | false => {
  const user = GetUserIfLoggedIn(identity);
  if (!user) {
    return false;
  }
  const org = user.organisations.find((o) => o.active);
  if (org) {
    return org;
  }
  return false;
};

const setActiveOrganisation = (
  organisations: Organisation[],
  organisation?: Organisation
): Organisation[] => {
  if (!organisation) {
    // Pick the first accessible org, not just the first org
    const orgs: Organisation[] = organisations.map((o) => {
      return {
        name: o.name,
        organisationId: o.organisationId,
        active: false,
        subscription: o.subscription,
        hasAccess: o.hasAccess,
      };
    });

    const firstAccessibleIndex = orgs.findIndex((o) => o.hasAccess !== false);
    if (firstAccessibleIndex >= 0) {
      orgs[firstAccessibleIndex].active = true;
    }
    // If no accessible orgs, don't set any as active

    return orgs;
  }

  const f = (o: Organisation, s: Organisation): Organisation => {
    return {
      name: o.name,
      organisationId: o.organisationId,
      active: o.organisationId === s.organisationId && o.hasAccess !== false, // Only set active if accessible
      subscription: o.subscription,
      hasAccess: o.hasAccess,
    };
  };
  const orgs = organisations.map((o) => f(o, organisation));

  // If we ended up with no active orgs (saved org not accessible), pick first accessible one
  if (!orgs.find((o) => o.active === true)) {
    const firstAccessibleIndex = orgs.findIndex((o) => o.hasAccess !== false);
    if (firstAccessibleIndex >= 0) {
      orgs[firstAccessibleIndex].active = true;
    }
  }

  return orgs;
};

export const switchOrganisation = async (
  organisation: Organisation,
  identityState: IdentityState,
  setUserState: Dispatch<SetStateAction<IdentityState>>,
  update: (props?: Partial<IntercomProps>) => void
) => {
  const user = GetUserIfLoggedIn(identityState);
  if (!user) {
    return;
  }

  // Prevent switching to a non-accessible org
  if (organisation.hasAccess === false) {
    console.warn(
      "Cannot switch to non-accessible organization:",
      organisation.name
    );
    return;
  }

  update({
    company: {
      companyId: organisation.organisationId,
      name: organisation.name,
    },
    customAttributes: {
      current_organisation: organisation.name,
    },
  });
  setUserState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    cognitoSub: user.cognitoSub,
    isAdmin: user.isAdmin, // TODO
    organisations: setActiveOrganisation(user.organisations, organisation),
    cognitoUser: user.cognitoUser,
    onboarding: user.onboarding,
    accessibleCount: user.accessibleCount,
    hasPendingInvitations: user.hasPendingInvitations,
    onboardingComplete: user.onboardingComplete,
    phoneNumber: user.phoneNumber,
    role: user.role,
    workPhone: user.workPhone,
    emailVerified: user.emailVerified,
    phoneNumberVerified: user.phoneNumberVerified,
    address: user.address,
  });
};
